import { useState, type ReactNode } from "react";
import type { ChartDTO, HourlyDTO } from "@easyshop/shared";
import { cx } from "@/components/ui";
import { eur } from "@/lib/format";

/** Une série de barres : valeur par créneau, null = créneau à venir (barre grise de 3 px). */
export interface BarSeries {
  key: string;
  label: ReactNode;
  /** Couleur CSS de la barre et de la pastille de légende. */
  color: string;
  values: (number | null)[];
  /** Total affiché dans la légende. */
  total: number;
}

/** Graduation « ronde » au-dessus du max : 1, 2 ou 5 × 10ⁿ. */
function niceTop(max: number): number {
  if (max <= 0) return 100;
  const p = 10 ** Math.floor(Math.log10(max));
  const step = [1, 2, 5, 10].find((m) => m * p >= max)! * p;
  return step;
}

/**
 * Histogramme à deux séries masquables (légende cliquable). Un toucher sur un
 * créneau l'affiche au-dessus du graphique ; l'échelle suit les séries visibles.
 */
export function SalesBars({
  title,
  series,
  ticks,
  slotLabels,
  initial,
}: {
  title: string;
  series: BarSeries[];
  /** Étiquette d'axe de chaque créneau (vide pour en sauter). */
  ticks: string[];
  /** Libellé d'un créneau sélectionné (« 13h–14h », « mar. 6 oct. »). */
  slotLabels: string[];
  /** Créneau sélectionné tant que l'utilisateur n'en a pas touché un. */
  initial: number;
}) {
  const [hidden, setHidden] = useState<Record<string, boolean>>({});
  // Créneau touché ; tant qu'il n'y en a pas, la sélection suit `initial` (qui bouge avec les données).
  const [picked, setPicked] = useState<number | null>(null);
  const sel = picked ?? initial;
  const visible = series.filter((s) => !hidden[s.key]);
  const top = niceTop(Math.max(0, ...visible.flatMap((s) => s.values.map((v) => v ?? 0))));
  const width = visible.length > 1 ? "40%" : "62%";
  const n = ticks.length;
  const at = Math.min(sel, n - 1);

  return (
    <div className="rounded-[22px] bg-white px-4 pb-[14px] pt-[18px]">
      <div className="text-[20px] font-bold tracking-[-0.3px]">{title}</div>
      <div className="mt-[10px] flex flex-wrap gap-2">
        {series.map((s) => (
          <button
            key={s.key}
            type="button"
            aria-pressed={!hidden[s.key]}
            onClick={() => setHidden((h) => ({ ...h, [s.key]: !h[s.key] }))}
            className={cx(
              "flex min-h-[32px] items-center gap-[6px] whitespace-nowrap rounded-full px-[10px] text-[13px] font-medium",
              hidden[s.key] ? "opacity-45" : "bg-soft-fill",
            )}
          >
            <span className="h-[9px] w-[9px] rounded-[3px]" style={{ background: s.color }} />
            {s.label}
            <span className="font-semibold tabular">{eur(s.total)}</span>
          </button>
        ))}
      </div>

      <div className="mt-[14px] flex min-h-[20px] flex-wrap items-baseline gap-x-[10px] text-[13px] text-muted">
        <span className="font-semibold text-ink">{slotLabels[at]}</span>
        {visible.map((s) =>
          s.values[at] == null ? null : (
            <span key={s.key}>
              <span style={{ color: s.color }}>●</span> <b className="font-semibold tabular text-ink">{eur(s.values[at])}</b>
            </span>
          ),
        )}
      </div>

      <div className="relative mt-2 h-[130px]">
        <div className="absolute inset-x-0 top-0 border-t border-dashed border-[oklch(0.9_0.01_150)]" />
        <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-[oklch(0.93_0.01_150)]" />
        <div className="absolute -top-4 right-0 text-[10px] tabular text-faint">{eur(top)}</div>
        <div className="relative flex h-full items-end gap-[3px]">
          {ticks.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={slotLabels[i]}
              onClick={() => setPicked(i)}
              className={cx("flex h-full min-w-0 flex-1 items-end justify-center gap-[2px] rounded-[6px]", i === at && "bg-[oklch(0.4_0.08_155/0.08)]")}
            >
              {visible.map((s) => {
                const v = s.values[i];
                return (
                  <span
                    key={s.key}
                    className="max-w-[10px] rounded-[3px_3px_1px_1px]"
                    style={{
                      width,
                      height: v == null ? 3 : `${Math.max(2, (v / top) * 100)}%`,
                      background: v == null ? "var(--bar-empty)" : s.color,
                    }}
                  />
                );
              })}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-[6px] flex gap-[3px]">
        {ticks.map((t, i) => (
          <span key={i} className="min-w-0 flex-1 text-center text-[10px] tabular text-faint">
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Ouverture affichée par défaut (10 h → 20 h), élargie si des ventes tombent en dehors. */
const OPEN = 10;
const CLOSE = 20;

function hourSpan(hourly: HourlyDTO): [number, number] {
  const sold = (h: number) => [hourly.current, hourly.previous].some((s) => (s[h] ?? 0) > 0);
  let from = OPEN;
  let to = CLOSE;
  for (let h = 0; h < OPEN; h++) if (sold(h)) { from = h; break; }
  for (let h = 23; h >= CLOSE; h--) if (sold(h)) { to = h + 1; break; }
  return [from, to];
}

const sumTo = (values: (number | null)[], upTo: number) => values.slice(0, upTo + 1).reduce<number>((s, v) => s + (v ?? 0), 0);

/**
 * Ventes par heure d'une journée. `mode` : « segments » (intégrées / franchises,
 * accueil réseau) ou « cmp » (la journée et la journée comparée, page boutique).
 * Le total de la journée comparée s'arrête à l'heure qu'il est (comparaison à heure égale).
 */
export function HourlyBars({
  hourly,
  mode,
  currentLabel,
  cmpLabel,
}: {
  hourly: HourlyDTO;
  mode: "segments" | "cmp";
  /** Libellé de la journée affichée (« Aujourd'hui », « lun. 28 sept. »). */
  currentLabel: string;
  /** Libellé de la journée comparée (« mer. 30 sept. »). */
  cmpLabel: string;
}) {
  const [from, to] = hourSpan(hourly);
  const hours = Array.from({ length: to - from }, (_, i) => from + i);
  const slice = (v: (number | null)[]) => hours.map((h) => v[h] ?? null);
  const now = new Date();
  const last = hourly.nowHour ?? 23;
  // Journée comparée « à heure égale » : heures pleines puis la part écoulée de l'heure en cours.
  const prevTotal =
    hourly.nowHour == null ? sumTo(hourly.previous, 23) : sumTo(hourly.previous, last - 1) + (hourly.previous[last] ?? 0) * (now.getMinutes() / 60);
  const at = hourly.nowHour == null ? "" : ` · à ${hourly.nowHour}h${String(now.getMinutes()).padStart(2, "0")}`;

  const series: BarSeries[] =
    mode === "segments"
      ? [
          { key: "int", label: "Intégrées", color: "var(--integrated)", values: slice(hourly.integrated), total: sumTo(hourly.integrated, last) },
          { key: "fra", label: "Franchises", color: "var(--franchise-vivid)", values: slice(hourly.franchise), total: sumTo(hourly.franchise, last) },
        ]
      : [
          { key: "cur", label: currentLabel, color: "var(--integrated)", values: slice(hourly.current), total: sumTo(hourly.current, last) },
          { key: "prev", label: `${cmpLabel}${at}`, color: "var(--cmp-bar)", values: slice(hourly.previous), total: prevTotal },
        ];

  // Créneau sélectionné au départ : l'heure en cours, sinon l'heure la plus forte.
  const totals = hours.map((h) => (hourly.current[h] ?? 0));
  const peak = totals.indexOf(Math.max(...totals));
  const initial = hourly.nowHour != null && hourly.nowHour >= from && hourly.nowHour < to ? hourly.nowHour - from : Math.max(0, peak);

  return (
    <SalesBars
      title="Ventes par heure"
      series={series}
      ticks={hours.map((h) => (h % 2 === 0 ? `${h}h` : ""))}
      slotLabels={hours.map((h) => `${h}h–${h + 1}h`)}
      initial={initial}
    />
  );
}

/**
 * Hors vue Jour : la courbe de la période en barres (jours de la semaine,
 * tranches de 7 jours du mois, mois de l'année), comparée à la période de référence.
 */
export function PeriodBars({ chart, title, currentLabel, cmpLabel }: { chart: ChartDTO; title: string; currentLabel: string; cmpLabel: string }) {
  const n = chart.labels.length;
  const current = Array.from({ length: n }, (_, i) => (i < chart.current.length ? chart.current[i] : null));
  const started = chart.current.length;
  // Total de la référence sur les mêmes créneaux que la période en cours.
  const prevTotal = chart.previous.slice(0, started).reduce((s, v) => s + v, 0);
  const total = chart.current.reduce((s, v) => s + v, 0);
  return (
    <SalesBars
      title={title}
      series={[
        { key: "cur", label: currentLabel, color: "var(--integrated)", values: current, total },
        { key: "prev", label: cmpLabel, color: "var(--cmp-bar)", values: chart.previous, total: prevTotal },
      ]}
      ticks={chart.axis}
      slotLabels={chart.labels}
      initial={Math.max(0, started - 1)}
    />
  );
}
